import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Home, ListChecks, FolderKanban, Users, Settings, LogOut, Receipt } from "lucide-react";
import { useAuth } from "@/lib/auth";

const NAV = [
  { to: "/", label: "Home Dashboard", icon: Home, end: true, testid: "nav-home" },
  { to: "/work-tracker", label: "Work Tracker", icon: ListChecks, testid: "nav-work-tracker" },
  { to: "/projects", label: "Project Dashboard", icon: FolderKanban, testid: "nav-projects" },
  { to: "/clients", label: "Client & Retainer Log", icon: Users, testid: "nav-clients" },
  { to: "/internal-ops", label: "Internal Ops", icon: Settings, testid: "nav-internal-ops" },
  { to: "/invoices", label: "Invoice Tracking", icon: Receipt, testid: "nav-invoices", adminOnly: true },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const initials = (user?.name || "?").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();

  return (
    <aside
      data-testid="app-sidebar"
      className="w-64 fixed left-0 top-0 h-screen bg-white border-r border-gray-200 flex flex-col"
    >
      <div className="px-6 py-6 border-b border-gray-100">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-md bg-navy text-white flex items-center justify-center font-bold text-sm tracking-tight">
            N
          </div>
          <div className="flex flex-col leading-tight">
            <span className="text-[15px] font-semibold text-gray-900 tracking-tight">Neev OS</span>
            <span className="text-[10px] text-gray-500 uppercase tracking-[0.18em]">Internal</span>
          </div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1">
        {NAV.filter(n => !n.adminOnly || user?.role === "admin").map(({ to, label, icon: Icon, end, testid }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                isActive
                  ? "bg-navy/5 text-navy"
                  : "text-gray-600 hover:bg-gray-50 hover:text-navy"
              }`
            }
          >
            <Icon className="w-4 h-4" strokeWidth={1.75} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-gray-100 p-3">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-9 h-9 rounded-full bg-navy/10 text-navy flex items-center justify-center text-xs font-semibold">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <div data-testid="sidebar-user-name" className="text-sm font-medium text-gray-900 truncate">{user?.name}</div>
            <div className="text-[11px] uppercase tracking-wider text-gray-500">
              {user?.role === "admin" ? "Admin" : "Team Member"}
            </div>
          </div>
          <button
            data-testid="sidebar-logout-btn"
            onClick={() => { logout(); nav("/login"); }}
            className="p-2 text-gray-400 hover:text-navy rounded transition-colors"
            title="Logout"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}
